import { provideZoneChangeDetection } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';

import { AppModule } from './app/app.module';
import * as Sentry from '@sentry/angular';
import {
  breadcrumbsIntegration,
  captureConsoleIntegration,
} from '@sentry/browser';
import { environment } from './environments/environment';

type ErrorTrackerConfig = {
  dsn: string;
  tunnel: string;
};

const sentryProd: ErrorTrackerConfig = {
  dsn: 'https://5f202ad2a7570de8a31600ce9b72e2a4@o4510906398015488.ingest.de.sentry.io/4510906401161296',
  tunnel: '/trace/api/4510906401161296/envelope/',
};

// TODO: Replace DSN and tunnel with actual values from your new Sentry QA project
const sentryQa: ErrorTrackerConfig = {
  dsn: 'https://7ddfa1971e807dd8f4316d22fe750477@o4510940725116928.ingest.us.sentry.io/4511862764666885',
  tunnel: '/trace/api/4511862764666885/envelope/',
};

const PROD_DOMAINS = [
  'app.iqigai.ai',
  'applite.iqigai.ai',
  'fractal.iqigai.ai',
];
const QA_DOMAINS = [
  'preprod.iqigai.ai',
  'qa.iqigai.ai',
  'qalite.iqigai.ai',
  'localhost',
  '127.0.0.1',
];

function getErrorTrackerConfig(): ErrorTrackerConfig {
  const hostname = window.location.hostname;
  if (PROD_DOMAINS.includes(hostname)) return sentryProd;
  if (QA_DOMAINS.includes(hostname)) return sentryQa;
  if (hostname.endsWith('.iqigai.ai')) return sentryProd;
  return sentryQa;
}

function getSentryEnvironment(): string {
  const hostname = window.location.hostname;
  if (hostname === 'preprod.iqigai.ai') return 'preprod';
  if (hostname === 'qa.iqigai.ai' || hostname === 'qalite.iqigai.ai')
    return 'qa';
  if (hostname === 'localhost' || hostname === '127.0.0.1')
    return 'development';
  return 'production';
}

function getSubdomainTag(): string {
  const hostname = window.location.hostname;
  if (hostname.endsWith('.iqigai.ai')) {
    return hostname.replace('.iqigai.ai', '');
  }
  return hostname;
}

const errorTrackerConfig = getErrorTrackerConfig();
const sentryEnvironment = getSentryEnvironment();

function readStoredUser(): { id?: string; email?: string } | null {
  try {
    const raw = localStorage.getItem('loggedInUser');
    if (!raw) return null;

    const user = JSON.parse(raw);
    if (!user?.id && !user?.email) return null;

    return {
      id: user.id != null ? String(user.id) : undefined,
      email: user.email,
    };
  } catch {
    return null;
  }
}

function ensureUserOnLog<T extends { attributes?: Record<string, unknown> }>(
  log: T,
): T {
  const attributes = (log.attributes = log.attributes ?? {});
  if (attributes['user.id'] || attributes['user.email']) return log;

  const stored = readStoredUser();
  if (!stored) return log;

  Sentry.setUser({ id: stored.id, email: stored.email });
  if (stored.id) attributes['user.id'] = stored.id;
  if (stored.email) attributes['user.email'] = stored.email;

  return log;
}

Sentry.init({
  dsn: errorTrackerConfig?.dsn,
  tunnel: errorTrackerConfig?.tunnel,

  sendDefaultPii: true,

  integrations: [
    breadcrumbsIntegration({ console: true }),
    captureConsoleIntegration({
      levels: ['error'],
    }),
    Sentry.browserTracingIntegration(),
  ],

  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.category === 'console') {
      const level = breadcrumb.level;
      if (level !== 'warning' && level !== 'error') {
        return null;
      }
    }
    return breadcrumb;
  },

  enabled: environment.enableSentry,
  enableLogs: true,
  beforeSendLog: ensureUserOnLog,

  tracesSampleRate: sentryEnvironment === 'production' ? 1.0 : 0.7,

  environment: sentryEnvironment,

  initialScope: {
    tags: {
      subdomain: getSubdomainTag(),
    },
  },

  tracePropagationTargets: ['localhost', /^https:\/\/.*\.iqigai\.ai\//],
});

const storedUser = readStoredUser();
if (storedUser) {
  Sentry.setUser({ id: storedUser.id, email: storedUser.email });
}

platformBrowserDynamic()
  .bootstrapModule(AppModule, {
    applicationProviders: [provideZoneChangeDetection()],
  })
  .catch((err) => console.error(err));
